import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  fetchCustomers,
  createCustomer,
  updateCustomer,
  hardDeleteCustomer,
  setCustomerActive,
} from "../../store/slices/customerSlice";
import { customerService } from "../../services/customerService";
import Icon from "../../components/Common/Icon";
import AdvancedTable from "../../components/Common/AdvancedTable";
import ConfirmationModal from "../../components/Common/ConfirmationModal";
import { useToast } from "../../context/ToastContext";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("tr-TR") : "—");
const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }) : "—";
const fmtMoney = (v) =>
  v != null ? `${Number(v).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺` : "—";

const emptyForm = { fullName: "", phone: "", email: "", notes: "" };

export default function CustomersPage() {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { items, isLoading, error } = useSelector((state) => state.customers);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("action") === "new") {
      openCreate();
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Ekle/Düzenle
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  // Detay kartı
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const [confirmModal, setConfirmModal] = useState({ isOpen: false });

  useEffect(() => {
    dispatch(fetchCustomers({ size: 200 }));
  }, [dispatch]);

  const refresh = () => dispatch(fetchCustomers({ size: 200 }));

  // --- ekle/düzenle ---
  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError(null);
    setModalOpen(true);
  };
  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      fullName: row.fullName ?? "",
      phone: row.phone ?? "",
      email: row.email ?? "",
      notes: row.notes ?? "",
    });
    setFormError(null);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!form.phone.trim()) {
      setFormError("Telefon numarası zorunludur.");
      return;
    }
    setSaving(true);
    const payload = {
      fullName: form.fullName.trim(),
      phone: form.phone.trim(),
      email: form.email.trim() || null,
      notes: form.notes.trim() || null,
    };
    try {
      if (editingId) {
        await dispatch(updateCustomer({ id: editingId, payload })).unwrap();
        showToast("Müşteri güncellendi.", "success");
      } else {
        await dispatch(createCustomer(payload)).unwrap();
        showToast("Müşteri eklendi.", "success");
      }
      closeModal();
      refresh();
    } catch (err) {
      setFormError(typeof err === "string" ? err : "İşlem başarısız.");
    } finally {
      setSaving(false);
    }
  };

  // --- detay ---
  const openDetail = async (row) => {
    setDetailOpen(true);
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await customerService.get(row.id);
      setDetail(res.data?.data ?? null);
    } catch {
      showToast("Detay yüklenemedi.", "error");
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  // --- sil / durum ---
  const triggerHardDelete = (row) => {
    setConfirmModal({
      isOpen: true,
      title: "Müşteriyi Kalıcı Sil",
      message: `${row.fullName} kalıcı olarak silinecek. Bu işlem geri alınamaz. Emin misiniz?`,
      type: "danger",
      onConfirm: () =>
        dispatch(hardDeleteCustomer(row.id))
          .unwrap()
          .then(() => showToast("Müşteri kalıcı silindi.", "success"))
          .catch((err) => showToast(err || "İşlem başarısız.", "error")),
    });
  };
  const toggleActive = (row) => {
    dispatch(setCustomerActive({ id: row.id, active: !row.active }))
      .unwrap()
      .then(() => showToast(row.active ? "Pasife alındı." : "Aktifleştirildi.", "success"))
      .catch((err) => showToast(err || "İşlem başarısız.", "error"));
  };

  const columns = [
    {
      key: "fullName",
      header: "Müşteri",
      type: "string",
      sortable: true,
      render: (val, row) => (
        <div>
          <span className="font-semibold text-on-surface block">{val}</span>
          <span className="text-[11px] text-on-surface-variant block">
            {row.phone || row.email || "—"}
          </span>
        </div>
      ),
    },
    {
      key: "visitCount",
      header: "Ziyaret",
      type: "number",
      sortable: true,
      render: (v) => v ?? 0,
    },
    {
      key: "totalSpent",
      header: "Toplam Harcama",
      type: "number",
      sortable: true,
      render: (v) => fmtMoney(v),
    },
    {
      key: "lastVisit",
      header: "Son Ziyaret",
      type: "date",
      sortable: true,
      render: (v) => fmtDate(v),
    },
    {
      key: "active",
      header: "Durum",
      sortable: true,
      render: (val) => (
        <span
          className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
            val
              ? "bg-success/15 text-success border border-success/20"
              : "bg-outline-variant/30 text-on-surface-variant border border-outline-variant/40"
          }`}
        >
          {val ? "Aktif" : "Pasif"}
        </span>
      ),
    },
    { key: "createdAt", header: "Eklenme", type: "date", sortable: true, render: (v) => fmtDate(v) },
  ];

  const renderActions = (c) => (
    <div className="flex items-center justify-end gap-1.5">
      <button
        onClick={() => openDetail(c)}
        title="Detay"
        className="p-1.5 rounded-lg text-primary hover:bg-primary/10 transition"
      >
        <Icon name="visibility" className="!text-[18px]" />
      </button>
      <button
        onClick={() => openEdit(c)}
        title="Düzenle"
        className="p-1.5 rounded-lg text-primary hover:bg-primary/10 transition"
      >
        <Icon name="edit" className="!text-[18px]" />
      </button>
      <button
        onClick={() => toggleActive(c)}
        title={c.active ? "Pasife Al" : "Aktifleştir"}
        className={`p-1.5 rounded-lg transition ${
          c.active ? "text-tier-gold hover:bg-tier-gold/10" : "text-success hover:bg-success/10"
        }`}
      >
        <Icon name={c.active ? "pause_circle" : "play_circle"} className="!text-[18px]" />
      </button>
      <button
        onClick={() => triggerHardDelete(c)}
        title="Kalıcı Sil"
        className="p-1.5 rounded-lg text-error hover:bg-error/10 transition"
      >
        <Icon name="delete_forever" className="!text-[18px]" />
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Başlık */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <Icon name="group" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-on-surface">Müşteriler</h1>
            <p className="text-on-surface-variant text-sm">
              Müşteri veritabanı ve ziyaret geçmişi.
            </p>
          </div>
        </div>

        <button
          onClick={openCreate}
          className="bg-primary text-white rounded-xl px-4 py-2 font-semibold text-sm hover:opacity-90 transition flex items-center gap-1.5"
        >
          <Icon name="add" className="!text-[18px]" /> Yeni Müşteri
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-error bg-error/10 border border-error/20 rounded-xl px-4 py-3">
          <Icon name="error" className="!text-[18px]" /> {error}
        </div>
      )}

      {isLoading ? (
        <div className="bg-surface-card rounded-3xl p-8 border border-outline-variant text-center text-on-surface-variant">
          Yükleniyor…
        </div>
      ) : (
        <AdvancedTable
          columns={columns}
          data={items}
          actions={renderActions}
          searchPlaceholder="Müşteride ara..."
        />
      )}

      {/* Ekle/Düzenle Modalı */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-surface-card rounded-3xl shadow-modal border border-outline-variant max-w-xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-outline-variant flex items-center justify-between bg-surface-container-low">
              <div className="flex items-center gap-2">
                <Icon name="group" className="text-primary !text-[24px]" />
                <h3 className="font-bold text-lg text-on-surface">
                  {editingId ? "Müşteriyi Düzenle" : "Yeni Müşteri"}
                </h3>
              </div>
              <button onClick={closeModal} className="p-1 rounded-full text-on-surface-variant hover:text-primary transition">
                <Icon name="close" className="!text-[22px]" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-4 flex-1 text-sm">
              {formError && (
                <div className="text-xs text-error bg-error/10 border border-error/20 rounded-xl px-3 py-2 flex items-center gap-1.5">
                  <Icon name="error" className="!text-[16px]" /> {formError}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Ad Soyad *</label>
                  <input
                    required
                    value={form.fullName}
                    onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                    className="w-full border border-outline-variant rounded-xl px-3 py-2 outline-none focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Telefon</label>
                  <input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="05xx xxx xx xx"
                    className="w-full border border-outline-variant rounded-xl px-3 py-2 outline-none focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold mb-1 text-on-surface-variant">E-posta</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full border border-outline-variant rounded-xl px-3 py-2 outline-none focus:border-primary"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Not</label>
                  <textarea
                    rows={3}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Alerji, tercih, geçmiş notları…"
                    className="w-full border border-outline-variant rounded-xl px-3 py-2 outline-none focus:border-primary resize-none"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={closeModal} className="border border-outline-variant bg-white text-on-surface hover:bg-surface-container-high transition rounded-xl px-4 py-2 font-semibold text-xs">
                  İptal
                </button>
                <button type="submit" disabled={saving} className="bg-primary text-white rounded-xl px-5 py-2 font-semibold text-xs hover:opacity-90 transition disabled:opacity-50 flex items-center gap-1.5">
                  {saving ? "Kaydediliyor…" : (<><Icon name="save" className="!text-[16px]" /> Kaydet</>)}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detay Kartı */}
      {detailOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-surface-card rounded-3xl shadow-modal border border-outline-variant max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-outline-variant flex items-center justify-between bg-surface-container-low">
              <div className="flex items-center gap-2">
                <Icon name="group" className="text-primary !text-[24px]" />
                <h3 className="font-bold text-lg text-on-surface">Müşteri Detay Kartı</h3>
              </div>
              <button onClick={() => setDetailOpen(false)} className="p-1 rounded-full text-on-surface-variant hover:text-primary transition">
                <Icon name="close" className="!text-[22px]" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto space-y-5 flex-1 text-sm">
              {detailLoading || !detail ? (
                <div className="py-8 text-center text-on-surface-variant">Yükleniyor…</div>
              ) : (
                <>
                  <div className="flex justify-between items-start bg-primary/5 border border-primary/10 rounded-2xl p-4">
                    <div>
                      <h4 className="text-xl font-bold text-primary">{detail.fullName}</h4>
                      <p className="text-xs text-on-surface-variant mt-0.5">
                        {detail.phone || detail.email || "İletişim bilgisi yok"}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${detail.active ? "bg-success/15 text-success" : "bg-outline-variant/30 text-on-surface-variant"}`}>
                      {detail.active ? "Aktif" : "Pasif"}
                    </span>
                  </div>

                  {/* Geçmiş özeti */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-surface-container-low rounded-2xl p-3 text-center">
                      <p className="text-lg font-bold text-on-surface">{detail.visitCount ?? 0}</p>
                      <p className="text-[11px] text-on-surface-variant">Ziyaret</p>
                    </div>
                    <div className="bg-surface-container-low rounded-2xl p-3 text-center">
                      <p className="text-lg font-bold text-on-surface">{fmtMoney(detail.totalSpent)}</p>
                      <p className="text-[11px] text-on-surface-variant">Toplam Harcama</p>
                    </div>
                    <div className="bg-surface-container-low rounded-2xl p-3 text-center">
                      <p className="text-lg font-bold text-on-surface">{detail.loyaltyPoints ?? 0}</p>
                      <p className="text-[11px] text-on-surface-variant">Sadakat Puanı</p>
                    </div>
                    <div className="bg-surface-container-low rounded-2xl p-3 text-center">
                      <p className="text-sm font-bold text-on-surface">{fmtDate(detail.lastVisit)}</p>
                      <p className="text-[11px] text-on-surface-variant">Son Ziyaret</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-surface-container-low rounded-2xl p-4 space-y-1.5 text-xs text-on-surface">
                      <h5 className="font-bold uppercase tracking-wider text-on-surface-variant mb-1">İletişim</h5>
                      <p><strong>E-posta:</strong> {detail.email || "—"}</p>
                      <p><strong>Telefon:</strong> {detail.phone || "—"}</p>
                    </div>
                    <div className="bg-surface-container-low rounded-2xl p-4 space-y-1.5 text-xs text-on-surface">
                      <h5 className="font-bold uppercase tracking-wider text-on-surface-variant mb-1">Not</h5>
                      <p className="whitespace-pre-wrap">{detail.notes || "—"}</p>
                    </div>
                  </div>

                  {/* Denetim */}
                  <div className="bg-surface-container-low rounded-2xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs text-on-surface">
                    <h5 className="font-bold uppercase tracking-wider text-on-surface-variant mb-1 sm:col-span-2 flex items-center gap-1">
                      <Icon name="history" className="!text-[16px] text-primary" /> Denetim
                    </h5>
                    <p><strong>Ekleyen:</strong> {detail.createdBy || "—"}</p>
                    <p><strong>Eklenme:</strong> {fmtDateTime(detail.createdAt)}</p>
                    <p><strong>Son Güncelleyen:</strong> {detail.updatedBy || "—"}</p>
                    <p><strong>Son Güncelleme:</strong> {fmtDateTime(detail.updatedAt)}</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={confirmModal.onConfirm}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}
